import { useCallback, useEffect, useState, type FC } from 'react';
import { Checkbox, Label, Section, Text } from '@highereducation/hen-ui';
import { useDynamicExperienceContext } from '../../providers/DynamicExperienceProvider';
import type { Option } from '../../types/types';

type Props = {
  hasGivenProspectSharingConsent?: boolean;
  tcpa?: string;
  title?: string;
  schoolId?: string;
  schoolName?: string;
  tcpaSecondParagraph?: string;
  providerName?: string;
  programFlow?: {
    providerProgramIdArray?: string[];
    tcpa?: string;
  }[];
};

const TcpaSection = ({
  tcpa,
  title = 'Terms and Conditions',
  schoolId = '',
  schoolName = '',
  tcpaSecondParagraph,
  providerName,
  programFlow,
}: Props) => {
  const dynamicExperienceContext = useDynamicExperienceContext();
  const [localConsent, setLocalConsent] = useState(true);

  const flowConsentOption =
    dynamicExperienceContext?.flowState?.['hasGivenThirdPartySharingConsent']
      ?.option;
  const flowConsentValue = Array.isArray(flowConsentOption)
    ? flowConsentOption[0]?.value
    : (flowConsentOption as Option | undefined)?.value;

  const normalizedFlowConsent =
    typeof flowConsentValue === 'boolean'
      ? flowConsentValue
      : flowConsentValue === 'true'
        ? true
        : flowConsentValue === 'false'
          ? false
          : undefined;

  const persistConsentInFlow = useCallback(
    (checked: boolean) => {
      if (!dynamicExperienceContext?.updateFlowState) return;
      dynamicExperienceContext.updateFlowState(
        'hasGivenThirdPartySharingConsent',
        {
          option: {
            optionId: 'hasGivenThirdPartySharingConsent',
            text: checked ? 'true' : 'false',
            value: checked,
            leadFieldAssociated: 'hasGivenThirdPartySharingConsent',
          },
          leadFieldAssociated: 'hasGivenThirdPartySharingConsent',
        },
      );
    },
    [dynamicExperienceContext],
  );

  useEffect(() => {
    if (normalizedFlowConsent === undefined) {
      persistConsentInFlow(true);
      setLocalConsent(true);
    } else {
      setLocalConsent(normalizedFlowConsent);
    }
  }, [normalizedFlowConsent, persistConsentInFlow, dynamicExperienceContext]);

  const handleConsentChange = (checked: boolean) => {
    setLocalConsent(checked);
    persistConsentInFlow(checked);
  };

  if (!tcpa || !tcpa.length) return <></>;

  const textColor = 'subtext';
  const secondParagraphHtml =
    tcpaSecondParagraph && schoolName.includes('Syracuse')
      ? tcpaSecondParagraph.split('\n')[0]
      : tcpaSecondParagraph;

  return (
    <Section
      as='section'
      padding='none'
      className='gap-0'
      data-tf-element-role='consent-language'
    >
      {title && (
        <Text
          tag='div'
          variant='l1'
          weight='bold'
          color={textColor}
          className='mb-xxs'
        >
          {title}
        </Text>
      )}

      <Text tag='div' variant='l2' color={textColor} className='mb-md'>
        <Text
          tag='span'
          variant='l1'
          weight='bold'
          className='font-bold'
          data-tf-element-role='consent-advertiser-name'
        >
          {schoolName}:
        </Text>{' '}
        &nbsp;
        <Label
          htmlFor='leadid_tcpa_disclosure'
          variant='subtext'
          className='font-normal'
          data-tf-element-role='consent-language'
        >
          <input type='hidden' id='leadid_tcpa_disclosure' />
          <TcpaSpan schoolId={schoolId} tcpa={tcpa} programFlow={programFlow} />
        </Label>
        {secondParagraphHtml && (
          <span
            className='mt-xxs block'
            dangerouslySetInnerHTML={{ __html: secondParagraphHtml }}
          />
        )}
        {providerName === 'TwoU' && (
          <Checkbox
            id='hasGivenThirdPartySharingConsent'
            className='mt-xs'
            checked={localConsent}
            onCheckedChange={(checked) => handleConsentChange(checked === true)}
            data-tf-element-role='consent-language'
            label={
              <Text tag='span' weight='bold'>
                Please contact me about these educational programs.
              </Text>
            }
          />
        )}
      </Text>
    </Section>
  );
};

// Replace get info or submit info cta label within the tcpa text
const replaceCtaLabelInTcpa = (ctaLabel: string, tcpa?: string) =>
  tcpa ? tcpa.replace(/(submit|get) info|send your request/gi, ctaLabel) : '';

type TcpaSpanProps = {
  schoolId: Props['schoolId'];
  tcpa: Props['tcpa'];
  programFlow: Props['programFlow'];
};

export const TcpaSpan: FC<TcpaSpanProps> = ({
  schoolId = '',
  tcpa,
  programFlow,
}) => {
  if (!tcpa && !programFlow) return null;
  const target = programFlow?.find((item) =>
    item.providerProgramIdArray?.includes(schoolId),
  );

  const targetTcpa = target?.tcpa ?? tcpa;
  const ctaLabel = 'Learn More';
  const formattedTcpa: string = replaceCtaLabelInTcpa(ctaLabel, targetTcpa);

  return (
    <Text tag='span' variant='l2' weight='regular' color='subtext'>
      <span dangerouslySetInnerHTML={{ __html: formattedTcpa }} />
    </Text>
  );
};

export default TcpaSection;
